import React, { useCallback, useMemo } from "react";
import cn from "clsx";
import {
  isEmpty,
  sortAlphabeticallyByPropFn,
  toArray
} from "../../utils/helpers";
import useT from "../../hooks/useT";
import Icon from "../Icon";
import Button from "../Button";
import InputSelectOption from "./InputSelectOption";
import { ReactSelectOptionObject } from "../InputSelectCustom";
import "./InputSelect.css";

export interface InputSelectProps {
  /** The ID of the input element. */
  id: string;
  /** The name of the input element. */
  name?: string;
  /** The current value of the input. */
  value: any;
  /** The options to render within the select input. */
  options: ReactSelectOptionObject[];
  /** Sort the options alphabetically. */
  sortOptionsAlphabetically?: boolean;
  /** Placeholder label. */
  placeholder?: string;
  /** Allow the user to easily clear the value. */
  clearable?: boolean;
  /** Mark if the input is disabled. */
  disabled?: boolean;
  /** Extra classes to assign to the input. */
  className?: string;
  /** The language used for translated labels (will default to i18next language if not set). */
  lang?: string;
  /** Event handler for when input is focused. */
  onFocus?: React.ReactEventHandler;
  /** Event handler for when input is blurred. */
  onBlur?: React.ReactEventHandler;
  /** Event handler for when input is changed. */
  onChange?: React.ReactEventHandler;
  /** Event handler for when input is cleared. */
  onClear?: React.ReactEventHandler;
  /** Ref to set the input element to. */
  ref?: React.Ref<HTMLSelectElement>;
}

export const InputSelect = React.forwardRef(
  (
    {
      id,
      name,
      value,
      options,
      sortOptionsAlphabetically = true,
      placeholder = "",
      clearable = false,
      disabled = false,
      lang,
      className,
      onFocus,
      onBlur,
      onChange,
      onClear
    }: InputSelectProps,
    ref?: React.Ref<HTMLSelectElement>
  ) => {
    const { t, i18n } = useT();
    const lng = lang || i18n.language;

    // Memoized values
    const selectOptions = useMemo(() => {
      // No need to sort
      if (!sortOptionsAlphabetically) {
        return options;
      }

      // Let the sorting ceremony commence!
      const _options = toArray(options);

      // Display any empty string or null options first
      const nullOptions = _options
        .filter(o => o.value === "" || o.value === null)
        .sort(sortAlphabeticallyByPropFn("label"));

      // Sort everything else after
      const restOptions = _options
        .filter(o => !nullOptions.includes(o))
        .sort(sortAlphabeticallyByPropFn("label"));

      // Combine the two
      return [...nullOptions, ...restOptions];
    }, [options]);

    // Event handlers
    const handleFocus: React.FocusEventHandler = useCallback(
      (event: React.FocusEvent) => {
        if (!!onFocus) {
          onFocus(event);
        }
      },
      [onFocus]
    );
    const handleBlur: React.FocusEventHandler = useCallback(
      (event: React.FocusEvent) => {
        if (!!onBlur) {
          onBlur(event);
        }
      },
      [onBlur]
    );
    const handleChange: React.ChangeEventHandler = useCallback(
      (event: React.ChangeEvent) => {
        if (!!onChange) {
          onChange(event);
        }
      },
      [onChange]
    );
    const handleClear: React.MouseEventHandler = useCallback(
      (event: React.MouseEvent) => {
        if (!!onClear) {
          onClear(event);
        }
      },
      [onClear]
    );

    return (
      <div
        className={cn(
          "input input-select",
          className,
          !!value && "has-value",
          !!clearable && "is-clearable"
        )}
      >
        <div className="input-control input-select-control">
          <select
            id={id}
            name={name}
            value={isEmpty(value) ? "" : value}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            ref={ref}
          >
            {selectOptions.map((optionProps, index) => (
              <InputSelectOption key={index} {...optionProps} />
            ))}
          </select>
          <div className="input-select-arrow">
            <Icon name="chevron-down" />
          </div>
        </div>
        {!!value && clearable ? (
          <div className="input-actions input-select-actions">
            <Button
              icon="clear"
              ariaLabel={t("general.buttons.clear.label", { lng })}
              className="clear-input"
              onClick={handleClear}
            />
          </div>
        ) : null}
      </div>
    );
  }
);

InputSelect.displayName = "InputSelect";

export default InputSelect;
